import React, { useMemo } from 'react';
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Business as BusinessIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  Hub as HubIcon,
  VpnKey as VpnKeyIcon,
  Http as WebhookIcon,
  Public as PublicIcon,
  Domain as DomainIcon,
  Email as EmailIcon,
  Tune as TuneIcon,
  GroupWork as GroupWorkIcon,
  Groups as GroupsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Payment as PaymentIcon,
  Store as StoreIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Percent as PercentIcon,
  Warehouse as WarehouseIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Map as MapIcon,
  LocationCity as LocationCityIcon,
  GppGood as GppGoodIcon,
  Palette as PaletteIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  adminHubCategories,
  filterAdminHubCategories,
  getAdminHubCategoryById,
  type AdminHubCategoryId,
} from '../../config/adminNavigation';
import ModuloFuncionalidades from '../ModuloFuncionalidades';
import { administrationMenuItem } from '../../config/adminNavigation';
import { navigateWithAdminBack } from '../../utils/adminNavigationContext';
import {
  effectiveUserRoles,
  isSuperAdminRole,
} from '../../utils/sistemaAuthz';

const CATEGORY_ICONS: Record<AdminHubCategoryId, React.ReactNode> = {
  'mi-empresa': <BusinessIcon />,
  modulos: <ExtensionIcon />,
  'usuarios-cajas': <PeopleIcon />,
  'ventas-comprobantes': <ReceiptIcon />,
  'depositos-logistica': <LocalShippingIcon />,
  integraciones: <HubIcon />,
  'moneda-domicilios': <PublicIcon />,
  tenants: <DomainIcon />,
};

const ITEM_ICONS: Record<string, React.ReactNode> = {
  '/sistema/companias': <BusinessIcon />,
  '/sistema/certificados-arca': <GppGoodIcon />,
  '/configuraciones/correo-salida': <EmailIcon />,
  '/configuraciones/parametros': <TuneIcon />,
  '/configuraciones/modulos': <ExtensionIcon />,
  '/configuraciones/variables-sistema': <TuneIcon />,
  '/configuraciones/apariencia': <PaletteIcon />,
  '/configuraciones/passwords': <VpnKeyIcon />,
  '/sistema/usuarios': <PeopleIcon />,
  '/sistema/grupo-caja-usuario': <GroupWorkIcon />,
  '/tesoreria/grupos-mcp': <GroupsIcon />,
  '/tesoreria/carteras': <AccountBalanceWalletIcon />,
  '/tesoreria/medios-mcp': <PaymentIcon />,
  '/configuraciones/sucursalesventa': <StoreIcon />,
  '/configuraciones/sucventa': <CategoryIcon />,
  '/sistema/tipo-formulario': <DescriptionIcon />,
  '/sistema/wsfe-tipcomp': <ReceiptIcon />,
  '/configuraciones/impuestos': <PercentIcon />,
  '/configuraciones/ubicaciones': <WarehouseIcon />,
  '/configuraciones/transportes': <LocalShippingIcon />,
  '/configuraciones/tokens-integracion': <VpnKeyIcon />,
  '/configuraciones/claves-agente': <VpnKeyIcon />,
  '/configuraciones/webhooks': <WebhookIcon />,
  '/sistema/monedas': <AttachMoneyIcon />,
  '/sistema/movmoneda': <TrendingUpIcon />,
  '/sistema/provincias': <MapIcon />,
  '/sistema/localidades': <LocationCityIcon />,
  '/sistema/tenants': <DomainIcon />,
  '/sistema/usuarios-maestros': <PeopleIcon />,
  '/sistema/biblioteca-informes': <DescriptionIcon />,
};

const ConfiguracionesHub: React.FC = () => {
  const navigate = useNavigate();
  const { categoriaId } = useParams<{ categoriaId?: string }>();
  const { user } = useAuth();
  const roles = effectiveUserRoles(user?.roles);
  const superAdmin = isSuperAdminRole(roles);

  const categorias = useMemo(
    () => filterAdminHubCategories(adminHubCategories, superAdmin),
    [superAdmin],
  );

  const categoriaActiva = useMemo(() => {
    if (!categoriaId) return undefined;
    return getAdminHubCategoryById(categoriaId, superAdmin);
  }, [categoriaId, superAdmin]);

  const volver = () => navigate('/menu-principal');

  if (categoriaId && !categoriaActiva) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f3f4f6', minHeight: '100vh' }}>
        <Alert severity="warning">La sección solicitada no existe o no tenés permiso para verla.</Alert>
      </Box>
    );
  }

  if (categoriaActiva && categoriaId) {
    return (
      <ModuloFuncionalidades
        cardScale={0.75}
        showBackButton={false}
        adminBackTo={`/configuraciones/${categoriaId}`}
        items={categoriaActiva.items.map((item) => ({
          text: item.text,
          path: item.path,
          description: item.description,
          icon: ITEM_ICONS[item.path] ?? <CategoryIcon />,
        }))}
      />
    );
  }

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        pt: 0,
        pb: { xs: 2, md: 3 },
        bgcolor: '#f3f4f6',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={volver} sx={{ textTransform: 'none' }}>
          Volver al menú principal
        </Button>
      </Box>

      <HubPrincipal categorias={categorias} navigate={navigate} />
    </Box>
  );
};

interface HubPrincipalProps {
  categorias: ReturnType<typeof filterAdminHubCategories>;
  navigate: NavigateFunction;
}

const HubPrincipal: React.FC<HubPrincipalProps> = ({ categorias, navigate }) => (
  <>
    <Grid container spacing={2.5}>
      {categorias.map((categoria) => (
        <Grid item xs={12} sm={6} lg={4} key={categoria.id}>
          <Card
            onClick={() => {
              if (categoria.items.length === 1) {
                navigateWithAdminBack(navigate, categoria.items[0].path, administrationMenuItem.path);
                return;
              }
              navigate(`/configuraciones/${categoria.id}`);
            }}
            sx={{
              borderRadius: 3,
              border: '1px solid #e5e7eb',
              boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.12)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: categoria.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: '#fff',
                  }}
                >
                  {CATEGORY_ICONS[categoria.id]}
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                  {categoria.title}
                </Typography>
                <Typography sx={{ color: '#6b7280', minHeight: 44 }}>
                  {categoria.description}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {categoria.items.map((item) => (
                  <Chip
                    key={item.path}
                    size="small"
                    variant="outlined"
                    label={item.text}
                    clickable
                    onClick={(event) => {
                      event.stopPropagation();
                      navigateWithAdminBack(navigate, item.path, administrationMenuItem.path);
                    }}
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#374151',
                      '&:hover': {
                        borderColor: categoria.color,
                        bgcolor: `${categoria.color}14`,
                        color: categoria.color,
                      },
                    }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </>
);

export default ConfiguracionesHub;
